import BoxSVG from "@assets/images/box-export.svg";
import externalLinkIcon from "@assets/images/external-link.svg";
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from "@material-ui/core";
import {
  formatConditionGroup,
  formatRouteTitle
} from "@saleor/changeOrderExport/utils";
import { IListMaterial } from "@saleor/changeOrderExport/views/ViewEditOrder/types";
import Checkbox from "@saleor/components/Checkbox";
import DecimalFormat from "@saleor/components/DecimalFormat";
import MyTypography from "@saleor/components/MyTypography";
import Skeleton from "@saleor/components/Skeleton";
import StickyHorizontalScrollBar from "@saleor/components/StickyHorizontalScrollBar";
import TableCellHeader from "@saleor/components/TableCellHeader";
import { TablePaginationCustom } from "@saleor/components/TablePaginationCustom";
import FullScreenButton from "@saleor/createOrderExport/components/FullScreenButton";
import { ExportOrderSortField } from "@saleor/createOrderExport/urls";
import EmptyProduct from "@saleor/createOrderExport/views/CartDetailExport/EmptyProduct";
import { ScgExportRejectReason } from "@saleor/graphql";
import useAppState from "@saleor/hooks/useAppState";
import { renderCollection } from "@saleor/misc";
import { ListProps, SortPage } from "@saleor/types";
import { getPlainTextFromEnum } from "@saleor/utils/message";
import { calculateTheWeight } from "@saleor/utils/number";
import { getArrowDirection } from "@saleor/utils/sort";
import classNames from "classnames";
import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import SVG from "react-inlinesvg";
import { FormattedNumber, useIntl } from "react-intl";

import useStyles from "./styles";

export interface MaterialItemTabDetailProps
  extends ListProps,
    SortPage<ExportOrderSortField> {
  latestPageItemNumber: number | null;
  onPreviousFirstPage: () => void;
  onNextLastPage: () => void;
  totalCount?: number;
  currency: string;
  listMaterial: IListMaterial[];
}

const totalCols = 20;

const MaterialItemTabDetail: React.FC<MaterialItemTabDetailProps> = ({
  settings,
  onUpdateListSettings,
  onNextPage,
  onPreviousPage,
  pageInfo,
  disabled,
  onSort,
  sort,
  onNextLastPage,
  latestPageItemNumber,
  onPreviousFirstPage,
  totalCount,
  listMaterial,
  currency
}) => {
  const intl = useIntl();
  const classes = useStyles();
  const [appState, dispatchAppState] = useAppState();
  const [reRender, setReRender] = useState<boolean>(false);
  const { fullScreen } = appState;
  const [tab, setTab] = useState<number>(0);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    dispatchAppState({
      payload: {
        tabMaterial: tab
      },
      type: "setTabMaterial"
    });
  }, [tab]);

  useEffect(() => {
    setReRender(prevState => !prevState);
  }, [fullScreen, listMaterial]);

  useEffect(() => {
    setReRender(prevState => !prevState);
    if (containerRef.current) {
      containerRef!.current!.scrollLeft = 0;
    }
  }, [tab]);

  const renderTabTable = () => (
    <div className={classes.tabBox}>
      <div
        className={classNames(
          classes.boxShadowTab,
          tab === 0 ? classes.z1 : classes.z0
        )}
      >
        <Button
          id="sales-condition-tab"
          data-testid="sales-condition-tab"
          onClick={() => setTab(0)}
          className={classNames(
            classes.tableTab,
            tab === 0 ? classes.active : classes.notActive
          )}
        >
          <MyTypography variant={"body2"}>
            {intl.formatMessage({
              defaultMessage: "Sales Condition"
            })}
          </MyTypography>
        </Button>
      </div>
      <div
        className={classNames(
          classes.boxShadowTab,
          tab === 1 ? classes.z1 : classes.z0
        )}
      >
        <Button
          id="order-details-tab"
          data-testid="order-details-tab"
          onClick={() => setTab(1)}
          className={classNames(
            classes.tableTab,
            classes.tabB,
            tab === 1 ? classes.active : classes.notActive
          )}
        >
          <MyTypography variant={"body2"}>
            {intl.formatMessage({
              defaultMessage: "Order Details"
            })}
          </MyTypography>
        </Button>
      </div>
    </div>
  );

  const renderTabA = () => (
    <>
      <TableCellHeader
        width={120}
        style={{ minWidth: "120px", borderRight: 0 }}
      >
        <MyTypography variant={"subtitle3"}>
          {intl.formatMessage({ defaultMessage: "จำนวน" })}
        </MyTypography>
      </TableCellHeader>
      <TableCellHeader width={72} style={{ minWidth: "72px" }}>
        <MyTypography variant={"body3"}>
          {intl.formatMessage({ defaultMessage: "หน่วย" })}
        </MyTypography>
      </TableCellHeader>
      <TableCellHeader
        width={80}
        textAlign={"right"}
        style={{ minWidth: "80px" }}
        className={classes.noBorderRight}
      >
        <MyTypography variant={"subtitle3"}>
          {intl.formatMessage({ defaultMessage: "น้ำหนัก" })}
        </MyTypography>
      </TableCellHeader>
      <TableCellHeader
        width={128}
        style={{ minWidth: "128px" }}
        textAlign={"center"}
      >
        <MyTypography variant={"body3"}>
          {intl.formatMessage({ defaultMessage: "หน่วย" })}
        </MyTypography>
      </TableCellHeader>
      <TableCellHeader width={128} style={{ minWidth: "128px" }}>
        <MyTypography variant={"subtitle3"}>
          {intl.formatMessage({ defaultMessage: "Item Cat (EO)" })}
        </MyTypography>
      </TableCellHeader>
      <TableCellHeader
        width={128}
        style={{ minWidth: "128px" }}
        textAlign={"right"}
      >
        <MyTypography variant={"subtitle3"}>
          {intl.formatMessage({ defaultMessage: "ราคา/หน่วย" })}
        </MyTypography>
      </TableCellHeader>
      <TableCellHeader width={88} style={{ minWidth: "88px" }}>
        <MyTypography variant={"subtitle3"}>
          {intl.formatMessage({ defaultMessage: "Currency" })}
        </MyTypography>
      </TableCellHeader>
      <TableCellHeader width={128} style={{ minWidth: "128px" }}>
        <MyTypography variant={"subtitle3"}>
          {intl.formatMessage({ defaultMessage: "Plant" })}
        </MyTypography>
      </TableCellHeader>
      <TableCellHeader width={128} style={{ minWidth: "128px" }}>
        <MyTypography variant={"subtitle3"}>
          {intl.formatMessage({ defaultMessage: "Ref. PI Stock" })}
        </MyTypography>
      </TableCellHeader>
      <TableCellHeader width={190} style={{ minWidth: "190px" }}>
        <MyTypography variant={"subtitle3"}>
          {intl.formatMessage({ defaultMessage: "Original Date" })}
        </MyTypography>
      </TableCellHeader>
      <TableCellHeader width={210} style={{ minWidth: "210px" }}>
        <MyTypography variant={"subtitle3"}>
          {intl.formatMessage({ defaultMessage: "Request Date" })}
        </MyTypography>
      </TableCellHeader>
      <TableCellHeader
        width={190}
        style={{ minWidth: "190px" }}
        className={classes.borderRight}
      >
        <MyTypography variant={"subtitle3"}>
          {intl.formatMessage({ defaultMessage: "Confirm Date" })}
        </MyTypography>
      </TableCellHeader>

      <TableCellHeader width={190} style={{ minWidth: "190px" }}>
        <MyTypography variant={"subtitle3"}>
          {intl.formatMessage({ defaultMessage: "Item Status" })}
        </MyTypography>
      </TableCellHeader>
      <TableCellHeader width={190} style={{ minWidth: "190px" }}>
        <MyTypography variant={"subtitle3"}>
          {intl.formatMessage({ defaultMessage: "PO No. (Outsource)" })}
        </MyTypography>
      </TableCellHeader>
      <TableCellHeader width={190} style={{ minWidth: "190px" }}>
        <MyTypography variant={"subtitle3"}>
          {intl.formatMessage({ defaultMessage: "PO Status (Outsource)" })}
        </MyTypography>
      </TableCellHeader>
      <TableCellHeader>
        {intl.formatMessage({ defaultMessage: "(P)" })}
      </TableCellHeader>
    </>
  );

  const renderTabB = () => (
    <>
      <TableCellHeader width={320} style={{ minWidth: "320px" }} rowSpan={2}>
        <MyTypography variant={"subtitle3"}>
          {intl.formatMessage({ defaultMessage: "Route" })}
        </MyTypography>
      </TableCellHeader>
      <TableCellHeader
        width={"160px"}
        style={{ minWidth: "160px" }}
        rowSpan={2}
      >
        <MyTypography variant={"subtitle3"}>
          {intl.formatMessage({ defaultMessage: "Condition Group 1" })}
        </MyTypography>
      </TableCellHeader>
      <TableCellHeader width={160} style={{ minWidth: "160px" }} rowSpan={2}>
        <MyTypography variant={"subtitle3"}>
          {intl.formatMessage({ defaultMessage: "Material Group 2" })}
        </MyTypography>
      </TableCellHeader>
      <TableCellHeader
        width={112}
        textAlign={"center"}
        style={{ minWidth: "112px" }}
        rowSpan={2}
      >
        <MyTypography variant={"subtitle3"} align="center">
          {intl.formatMessage({ defaultMessage: "Commission (%)" })}
        </MyTypography>
      </TableCellHeader>
      <TableCellHeader
        width={112}
        style={{ minWidth: "112px" }}
        textAlign={"center"}
        rowSpan={2}
      >
        <MyTypography variant={"subtitle3"} align="center">
          {intl.formatMessage({ defaultMessage: "Commission amount" })}
        </MyTypography>
      </TableCellHeader>
      <TableCellHeader
        width={112}
        style={{ minWidth: "112px" }}
        textAlign={"center"}
        rowSpan={2}
      >
        <MyTypography variant={"subtitle3"}>
          {intl.formatMessage({ defaultMessage: "Com. Unit" })}
        </MyTypography>
      </TableCellHeader>
      <TableCellHeader
        width={280}
        style={{ minWidth: "280px" }}
        textAlign={"center"}
        className={classes.customCell}
        colSpan={3}
      >
        <MyTypography variant={"subtitle3"}>
          {intl.formatMessage({ defaultMessage: "Delivery Tolerance" })}
        </MyTypography>
      </TableCellHeader>
      <TableCellHeader
        width={112}
        style={{ minWidth: "112px" }}
        align={"center"}
        rowSpan={2}
      >
        <MyTypography variant={"subtitle3"}>
          {intl.formatMessage({ defaultMessage: "Roll Diameter" })}
        </MyTypography>
      </TableCellHeader>
      <TableCellHeader
        width={112}
        style={{ minWidth: "112px" }}
        align={"center"}
        rowSpan={2}
      >
        <MyTypography variant={"subtitle3"}>
          {intl.formatMessage({ defaultMessage: "Roll Core Diameter" })}
        </MyTypography>
      </TableCellHeader>
      <TableCellHeader
        width={112}
        style={{ minWidth: "112px" }}
        textAlign={"center"}
        rowSpan={2}
      >
        <MyTypography variant={"subtitle3"}>
          {intl.formatMessage({ defaultMessage: "No. of Rolls" })}
        </MyTypography>
      </TableCellHeader>
      <TableCellHeader
        width={112}
        style={{ minWidth: "112px" }}
        align={"center"}
        rowSpan={2}
      >
        <MyTypography variant={"subtitle3"}>
          {intl.formatMessage({ defaultMessage: "Ream/Roll per Pallet" })}
        </MyTypography>
      </TableCellHeader>
      <TableCellHeader
        width={112}
        style={{ minWidth: "112px" }}
        textAlign={"center"}
        rowSpan={2}
      >
        <MyTypography variant={"subtitle3"}>
          {intl.formatMessage({ defaultMessage: "Pallet Size" })}
        </MyTypography>
      </TableCellHeader>
      <TableCellHeader
        width={112}
        style={{ minWidth: "112px" }}
        textAlign={"center"}
        rowSpan={2}
      >
        <MyTypography variant={"subtitle3"}>
          {intl.formatMessage({ defaultMessage: "Pallet No." })}
        </MyTypography>
      </TableCellHeader>
      <TableCellHeader
        width={112}
        style={{ minWidth: "112px" }}
        align={"center"}
        rowSpan={2}
      >
        <MyTypography variant={"subtitle3"}>
          {intl.formatMessage({ defaultMessage: "No. of Package" })}
        </MyTypography>
      </TableCellHeader>
      <TableCellHeader
        width={440}
        style={{ minWidth: "440px" }}
        textAlign={"center"}
        rowSpan={2}
      >
        <MyTypography variant={"subtitle3"}>
          {intl.formatMessage({ defaultMessage: "Packing List" })}
        </MyTypography>
      </TableCellHeader>
      <TableCellHeader
        width={200}
        style={{ minWidth: "200px" }}
        textAlign={"center"}
        rowSpan={2}
      >
        <MyTypography variant={"subtitle3"}>
          {intl.formatMessage({ defaultMessage: "Shipping Point" })}
        </MyTypography>
      </TableCellHeader>
      <TableCellHeader
        width={440}
        style={{ minWidth: "440px" }}
        textAlign={"center"}
        rowSpan={2}
      >
        <MyTypography variant={"subtitle3"}>
          {intl.formatMessage({ defaultMessage: "Remark" })}
        </MyTypography>
      </TableCellHeader>
    </>
  );

  const isContainer = item => item?.itemCategory === "ZKC0";

  const isK01K09 = item =>
    item.materialGroup1?.includes("K01") ||
    item.materialGroup1?.includes("K09");

  const isCancelItem = item => item.rejectReason === ScgExportRejectReason.YES;

  return (
    <>
      <div hidden={fullScreen}>
        <MyTypography variant="subtitle1" className={classes.header}>
          <SVG width={24} height={24} className={classes.svg} src={BoxSVG} />
          {intl.formatMessage({ defaultMessage: "รายละเอียดสินค้า" })}
        </MyTypography>
      </div>
      {listMaterial?.length ? (
        <>
          <div className={classes.topBox}>
            <FullScreenButton />
          </div>
          <div
            className={classes.tableRoot}
            style={{ overflowX: "auto" }}
            ref={containerRef}
          >
            {renderTabTable()}
            <Table>
              <TableHead>
                <TableRow>
                  <TableCellHeader
                    rowSpan={2}
                    textAlign={"center"}
                    className={classNames(
                      classes.borderLeft,
                      classes.freezeCell,
                      classes.itemNoCell
                    )}
                    direction={
                      sort.sort === ExportOrderSortField.item_no
                        ? getArrowDirection(sort.asc)
                        : undefined
                    }
                    arrowPosition="right"
                    onClick={() => onSort(ExportOrderSortField.item_no)}
                  >
                    <MyTypography variant={"subtitle3"}>
                      {intl.formatMessage({ defaultMessage: "Item No." })}
                    </MyTypography>
                  </TableCellHeader>
                  <TableCellHeader
                    className={classes.freezeCell1}
                    style={{ width: "180px", minWidth: "180px" }}
                    rowSpan={2}
                  >
                    <MyTypography variant={"subtitle3"}>
                      {intl.formatMessage({ defaultMessage: "Mat. Code" })}
                    </MyTypography>
                  </TableCellHeader>
                  <TableCellHeader
                    className={classes.freezeCell2}
                    style={{ width: "320px", minWidth: "320px" }}
                    rowSpan={2}
                  >
                    <MyTypography variant={"subtitle3"}>
                      {intl.formatMessage({
                        defaultMessage: "Mat. Description"
                      })}
                    </MyTypography>
                  </TableCellHeader>
                  {tab === 0 ? renderTabA() : renderTabB()}
                </TableRow>
                {tab === 1 && (
                  <TableRow>
                    <TableCellHeader
                      width={93}
                      style={{ minWidth: "93px" }}
                      className={classNames(
                        classes.customCell,
                        classes.noBorderRight,
                        classes.thDelivery
                      )}
                    >
                      <span className={classes.delivery}>Under (%)</span>
                    </TableCellHeader>
                    <TableCellHeader
                      width={93}
                      textAlign={"center"}
                      style={{ minWidth: "93px" }}
                      className={classNames(
                        classes.customCell,
                        classes.noBorderRight,
                        classes.thDelivery
                      )}
                    >
                      <span className={classes.delivery}>Over (%)</span>
                    </TableCellHeader>
                    <TableCellHeader
                      width={100}
                      style={{ minWidth: "100" }}
                      className={classNames(
                        classes.borderRight,
                        classes.customCell,
                        classes.thDelivery
                      )}
                    >
                      <span className={classes.delivery}>Unlimited</span>
                    </TableCellHeader>
                  </TableRow>
                )}
              </TableHead>
              <TableBody>
                {(listMaterial && listMaterial.length) > 0 ? (
                  renderCollection(listMaterial, line => (
                    <TableRow key={line.itemNo}>
                      <TableCell
                        className={classNames(
                          classes.borderLeft,
                          classes.freezeCell
                        )}
                        align={"center"}
                      >
                        <MyTypography variant={"body3"} align={"center"}>
                          {line.itemNo}
                        </MyTypography>
                      </TableCell>
                      <TableCell className={classes.freezeCell1}>
                        <MyTypography variant={"body3"}>
                          {line.material}
                        </MyTypography>
                      </TableCell>
                      <TableCell
                        className={classes.freezeCell2}
                        data-testid="materialDesc-cell"
                      >
                        <MyTypography variant={"body3"}>
                          {line.materialDesc}
                        </MyTypography>
                        {isCancelItem(line) ? (
                          <MyTypography
                            variant={"caption"}
                            className={classes.boxCancel}
                          >
                            Cancel 93
                          </MyTypography>
                        ) : (
                          ""
                        )}
                      </TableCell>

                      {tab === 0 ? (
                        <>
                          <TableCell
                            style={{ minWidth: "112px" }}
                            width={112}
                            className={classes.noBorderRight}
                          >
                            <MyTypography variant={"body3"}>
                              <FormattedNumber value={Number(line?.quantity)} />
                            </MyTypography>
                          </TableCell>
                          <TableCell align={"center"}>
                            <MyTypography variant={"body3"}>
                              {isContainer(line) ? "EA" : "ROL"}
                            </MyTypography>
                          </TableCell>
                          <TableCell
                            align={"center"}
                            className={classes.noBorderRight}
                          >
                            <MyTypography variant={"body3"}>
                              {isContainer(line) ? (
                                "-"
                              ) : (
                                <DecimalFormat
                                  value={Number(
                                    calculateTheWeight(
                                      line.weightUnit,
                                      Number(line.saleQtyFactor),
                                      Number(line.quantity)
                                    )?.toString()
                                  )}
                                  isGrayDecimal={false}
                                  countDecimal={3}
                                />
                              )}
                            </MyTypography>
                          </TableCell>
                          <TableCell align={"center"}>
                            <MyTypography variant={"body3"}>
                              {isContainer(line)
                                ? ""
                                : line.weightUnit ?? "TON"}
                            </MyTypography>
                          </TableCell>
                          <TableCell align={"center"}>
                            <MyTypography variant={"body3"}>
                              {line.itemCategory}
                            </MyTypography>
                          </TableCell>
                          <TableCell align={"right"}>
                            {isContainer(line) ? (
                              ""
                            ) : (
                              <DecimalFormat
                                value={Number(line?.conditionRate)}
                                isGrayDecimal={false}
                                countDecimal={2}
                              />
                            )}
                          </TableCell>
                          <TableCell>
                            <MyTypography variant={"body3"}>
                              {isContainer(line) ? "" : currency}
                            </MyTypography>
                          </TableCell>
                          <TableCell>
                            <MyTypography variant={"body3"}>
                              {line.plant}
                            </MyTypography>
                          </TableCell>
                          <TableCell>
                            <MyTypography variant={"body3"}>
                              {line.refPiStock}
                            </MyTypography>
                          </TableCell>
                          <TableCell>
                            <MyTypography variant={"body3"}>
                              {line?.originalRequestDate
                                ? line.originalRequestDate
                                : "-"}
                            </MyTypography>
                          </TableCell>
                          <TableCell align={"center"}>
                            <MyTypography variant={"body3"}>
                              {line?.requestDate
                                ? moment(line.requestDate).format("DD/MM/YYYY")
                                : ""}
                            </MyTypography>
                          </TableCell>
                          <TableCell align={"center"}>
                            <MyTypography variant={"body3"}>
                              {line?.confirmDate
                                ? moment(line.confirmDate).format("DD/MM/YYYY")
                                : ""}
                            </MyTypography>
                          </TableCell>

                          <TableCell>
                            <MyTypography variant={"body3"}>
                              {getPlainTextFromEnum(line?.itemStatusEn)}
                            </MyTypography>
                          </TableCell>
                          <TableCell>
                            <MyTypography variant={"body3"}>
                              {line?.poSubcontract}
                            </MyTypography>
                          </TableCell>
                          <TableCell>
                            <MyTypography variant={"body3"}>
                              {line?.poStatus}
                            </MyTypography>
                          </TableCell>
                          <TableCell>
                            <Button
                              disabled
                              className={classes.button}
                              variant="contained"
                              color="secondary"
                            >
                              <SVG src={externalLinkIcon} />
                            </Button>
                          </TableCell>
                        </>
                      ) : (
                        <>
                          <TableCell>
                            <MyTypography variant={"body3"}>
                              {formatRouteTitle(line)}
                            </MyTypography>
                          </TableCell>
                          <TableCell>
                            <MyTypography variant={"body3"}>
                              {formatConditionGroup(line)}
                            </MyTypography>
                          </TableCell>
                          <TableCell>
                            <MyTypography variant={"body3"}>
                              {line?.materialGroup1}
                            </MyTypography>
                          </TableCell>
                          <TableCell>
                            <MyTypography variant={"body3"}>
                              {line?.commission}
                            </MyTypography>
                          </TableCell>
                          <TableCell>
                            <MyTypography variant={"body3"}>
                              {line?.commissionAmount ? (
                                <DecimalFormat
                                  value={Number(line?.commissionAmount)}
                                  isGrayDecimal={false}
                                  countDecimal={3}
                                />
                              ) : (
                                ""
                              )}
                            </MyTypography>
                          </TableCell>
                          <TableCell>
                            <MyTypography variant={"body3"}>
                              {line?.commissionAmount
                                ? line?.commissionUnit
                                : ""}
                            </MyTypography>
                          </TableCell>
                          <TableCell className={classes.noBorderRight}>
                            <MyTypography variant={"body3"}>
                              {line?.deliveryTolUnlimited
                                ? "0"
                                : line?.deliveryTolUnder}
                            </MyTypography>
                          </TableCell>
                          <TableCell className={classes.noBorderRight}>
                            <MyTypography variant={"body3"}>
                              {line?.deliveryTolUnlimited
                                ? "0"
                                : line?.deliveryTolOver}
                            </MyTypography>
                          </TableCell>
                          <TableCell align={"center"}>
                            <span className={classes.delivery}>
                              <Checkbox
                                size={"small"}
                                disabled
                                name={"deliveryTolUnlimited"}
                                checked={line?.deliveryTolUnlimited}
                              />
                            </span>
                          </TableCell>
                          <TableCell>
                            <MyTypography variant={"body3"}>
                              {isK01K09(line) || isContainer(line)
                                ? ""
                                : line?.rollDiameter}
                            </MyTypography>
                          </TableCell>
                          <TableCell>
                            <MyTypography variant={"body3"}>
                              {isK01K09(line) || isContainer(line)
                                ? ""
                                : line.rollCoreDiameter}
                            </MyTypography>
                          </TableCell>
                          <TableCell>
                            <MyTypography variant={"body3"}>
                              {isK01K09(line) || isContainer(line)
                                ? ""
                                : line?.rollQuantity}
                            </MyTypography>
                          </TableCell>
                          <TableCell>
                            <MyTypography variant={"body3"}>
                              {isK01K09(line) || isContainer(line)
                                ? ""
                                : line.reamRollPerPallet}
                            </MyTypography>
                          </TableCell>
                          <TableCell>
                            <MyTypography variant={"body3"}>
                              {isK01K09(line) || isContainer(line)
                                ? ""
                                : line?.palletSize}
                            </MyTypography>
                          </TableCell>
                          <TableCell>
                            <MyTypography variant={"body3"}>
                              {isK01K09(line) || isContainer(line)
                                ? ""
                                : line?.palletNo}
                            </MyTypography>
                          </TableCell>
                          <TableCell>
                            <MyTypography variant={"body3"}>
                              {isK01K09(line) || isContainer(line)
                                ? ""
                                : line?.noOfPackage}
                            </MyTypography>
                          </TableCell>
                          <TableCell>
                            <MyTypography variant={"body3"}>
                              {isK01K09(line) || isContainer(line)
                                ? ""
                                : line?.packingList}
                            </MyTypography>
                          </TableCell>
                          <TableCell>
                            <MyTypography variant={"body3"}>
                              {line?.shippingPoint}
                            </MyTypography>
                          </TableCell>
                          <TableCell>
                            <MyTypography variant={"body3"}>
                              {line?.remark}
                            </MyTypography>
                          </TableCell>
                        </>
                      )}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={totalCols}>
                      <Skeleton />
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            {listMaterial && listMaterial.length ? (
              <StickyHorizontalScrollBar
                container={containerRef}
                reRender={reRender}
                bottom={fullScreen ? 0 : 165}
              />
            ) : null}
          </div>
          <TablePaginationCustom
            hasNextPage={pageInfo && !disabled ? pageInfo.hasNextPage : false}
            hasPreviousPage={
              pageInfo && !disabled ? pageInfo.hasPreviousPage : false
            }
            onNextPage={onNextPage}
            onPreviousPage={onPreviousPage}
            totalCount={totalCount}
            selectColor="#007E92"
            settings={settings}
            onUpdateListSettings={onUpdateListSettings}
            isBackFistPage
            isNextLastPage
            latestPageItemNumber={latestPageItemNumber}
            onNextLastPage={onNextLastPage}
            onPreviousFirstPage={onPreviousFirstPage}
            disabled={disabled}
          />
        </>
      ) : null}
      {!listMaterial?.length ? <EmptyProduct hideTotal /> : null}
    </>
  );
};

export default MaterialItemTabDetail;
